/**
 * @fileoverview Checkout Form Custom Hooks
 *
 * This file contains specialized React hooks for managing checkout form
 * state, validation, persistence, and integration with the API.
 *
 * Core Hooks:
 * - useCheckoutForm: Main form state management with validation and persistence
 * - useCheckoutTotal: Handles total calculations and updates parent component
 *
 * Key Features:
 * - Form data persistence with localStorage (24-hour expiration)
 * - Real-time field validation with error state management
 * - Collapsible section state management
 * - Country-specific field logic (USA state handling)
 * - Automatic merging of persisted data with API responses
 * - Form reset and cleanup capabilities
 * - Integration with external validation library (Yup)
 *
 * State Management:
 * - Form data with immediate updates and persistence
 * - Validation errors with field-level feedback
 * - UI state for collapsible sections and loading states
 * - Integration points for parent component communication
 */

import { useState, useEffect } from "react";
import { CheckoutApiResponse, CheckoutFieldMetadata } from "./CheckoutForm.types";
import { useCheckoutFormPersistence, mergePersistedWithApiData } from "./CheckoutForm.persistence";
import { validateCheckoutForm, validateField, ValidationErrors } from "./CheckoutForm.validation";
import {
  getSelectedSite,
  populateAddressFromSite,
  isAutoSelectSiteEnabled,
} from "./CheckoutForm.utils";

/**
 * Main checkout form management hook
 *
 * Handles all aspects of form state including data persistence, validation,
 * field changes, and integration with the checkout API.
 *
 * @param checkoutData - API response containing form metadata and initial data
 * @returns Object containing form state and control functions
 */
export const useCheckoutForm = (checkoutData: CheckoutApiResponse | null) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    AddressLines: true, // Initialize collapsible sections as collapsed
  });
  const [hasLoadedPersistedData, setHasLoadedPersistedData] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isValidating, setIsValidating] = useState(false);

  const { saveFormData, loadFormData, clearFormData, hasPersistedData } =
    useCheckoutFormPersistence();

  // Load persisted data when checkout data becomes available
  useEffect(() => {
    if (checkoutData && !hasLoadedPersistedData && hasPersistedData()) {
      const persistedData = loadFormData();
      if (Object.keys(persistedData).length > 0) {
        const mergedData = mergePersistedWithApiData(persistedData, checkoutData.details);
        setFormData(mergedData);
        setHasLoadedPersistedData(true);
      }
    }
  }, [checkoutData, loadFormData, hasPersistedData, hasLoadedPersistedData]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      saveFormData(formData);
    }
  }, [formData, saveFormData]);

  /**
   * Handles field value changes with validation and special logic
   *
   * @param fieldName - Name of the field being changed
   * @param value - New value for the field
   */
  const handleFieldChange = async (fieldName: string, value: any) => {
    const newFormData = {
      ...formData,
      [fieldName]: value,
    };

    // Handle site selection changes - auto-populate address fields
    if (fieldName === "SitesId" && checkoutData && isAutoSelectSiteEnabled(checkoutData)) {
      const selectedSite = getSelectedSite(checkoutData, value);
      if (selectedSite) {
        const addressData = populateAddressFromSite(selectedSite);
        Object.assign(newFormData, addressData);
      }
    }

    // If country changes, clear StateProvince and USStatesID to avoid conflicts
    if (fieldName === "CountriesId") {
      newFormData.StateProvince = "";
      newFormData.USStatesID = "";
    }

    setFormData(newFormData);

    // Validate the changed field
    try {
      const fieldError = await validateField(fieldName, value, newFormData, checkoutData);
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (fieldError) {
          newErrors[fieldName] = fieldError;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });

      // If country changed and we cleared state fields, also clear their validation errors
      if (fieldName === "CountriesId") {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.StateProvince;
          delete newErrors.USStatesID;
          return newErrors;
        });
      }
    } catch (error) {
      // console.warn("Field validation error:", error);
    }
  };

  /**
   * Gets the current value for a field, checking form data first then API data
   *
   * @param field - Field metadata to get value for
   * @returns Current field value or undefined if not found
   */
  const getFieldValue = (field: CheckoutFieldMetadata) => {
    if (!checkoutData) return undefined;

    // Check form data first, then fall back to API data
    if (formData[field.name] !== undefined) {
      return formData[field.name];
    }

    // Map API field names to data properties (convert from PascalCase)
    const dataKey = field.name.charAt(0).toLowerCase() + field.name.slice(1);
    return (checkoutData.details as any)[dataKey];
  };

  /**
   * Toggles the collapsed state of a collapsible section
   *
   * @param sectionKey - Key identifier for the section to toggle
   */
  const toggleCollapsibleSection = (sectionKey: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  /**
   * Validates the entire form and updates validation error state
   *
   * @returns Promise resolving to true if form is valid, false otherwise
   */
  const validateForm = async (): Promise<boolean> => {
    setIsValidating(true);
    try {
      const errors = await validateCheckoutForm(formData, checkoutData);
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    } catch (error) {
      // console.error("Form validation error:", error);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  /**
   * Gets the validation error message for a specific field
   *
   * @param fieldName - Name of the field to get error for
   * @returns Error message string or undefined if no error
   */
  const getFieldError = (fieldName: string): string | undefined => {
    return validationErrors[fieldName];
  };

  /**
   * Resets the form to its initial state and clears persisted data
   */
  const resetForm = () => {
    setFormData({});
    setCollapsedSections({
      AddressLines: true,
    });
    setValidationErrors({});
    clearFormData(); // Clear persisted data as well
  };

  return {
    formData,
    collapsedSections,
    validationErrors,
    isValidating,
    handleFieldChange,
    getFieldValue,
    toggleCollapsibleSection,
    validateForm,
    getFieldError,
    resetForm,
    clearFormData, // Expose clearFormData for external use (e.g., after order placement)
  };
};

/**
 * Hook for managing checkout total updates
 *
 * Monitors checkout data for total changes and notifies parent component
 * when the total value is updated from the API.
 *
 * @param checkoutData - API response containing total information
 * @param onTotalUpdate - Callback to notify parent of total changes
 */
export const useCheckoutTotal = (
  checkoutData: CheckoutApiResponse | null,
  onTotalUpdate: (total: number) => void
) => {
  useEffect(() => {
    if (checkoutData?.details.total) {
      onTotalUpdate(checkoutData.details.total);
    }
  }, [checkoutData?.details.total, onTotalUpdate]);
};
